/**
 * SuggestedLinkBadge - Floating approve/reject controls on suggested link lines
 *
 * Features:
 * - Positioned at midpoint of connection line
 * - Confidence percentage display
 * - Approve (checkmark) and reject (X) buttons
 * - Loading states during mutations
 */

import { motion } from 'framer-motion';
import { Check, Loader2, X } from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';

import { getConfidenceTextColor } from '../../utils/confidenceUtils';
import { useAsyncAction } from '../hooks/useAsyncAction';
import { useBadgePosition } from '../hooks/useBadgePosition';
import type { Point } from '../types/linking-view.types';

interface SuggestedLinkBadgeProps {
  linkId: string;
  confidence: number;
  position: Point;
  onApprove: () => void;
  onReject: () => void;
  isLoading?: boolean;
}

export const SuggestedLinkBadge: React.FC<SuggestedLinkBadgeProps> = ({
  linkId,
  confidence,
  position,
  onApprove,
  onReject,
  isLoading: externalLoading = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { ref, style } = useBadgePosition(position);
  const approveAction = useAsyncAction(onApprove, externalLoading);
  const rejectAction = useAsyncAction(onReject, externalLoading);

  const isLoading =
    externalLoading || approveAction.isLoading || rejectAction.isLoading;
  const confidencePercent = Math.round(confidence * 100);

  return (
    <motion.div
      ref={ref}
      className="pointer-events-auto absolute z-30"
      style={style}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      data-link-id={linkId}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Confidence badge - hidden on hover */}
      {!isHovered && (
        <motion.div
          className={cn(
            'rounded-full bg-white px-2 py-0.5 text-xs font-semibold shadow-md',
            'border border-amber-200',
            getConfidenceTextColor(confidence),
          )}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
        >
          {confidencePercent}%
        </motion.div>
      )}

      {/* Action buttons - shown on hover, replaces the badge */}
      {isHovered && (
        <motion.div
          className="flex items-center gap-0.5 rounded-full bg-white p-0.5 shadow-md"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
        >
          {/* Approve button */}
          <motion.button
            onClick={approveAction.execute}
            disabled={isLoading}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full transition-colors',
              'text-emerald-600 hover:bg-emerald-100',
              isLoading && 'cursor-not-allowed opacity-50',
            )}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            title="Approve link"
          >
            {approveAction.isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
          </motion.button>

          {/* Reject button */}
          <motion.button
            onClick={rejectAction.execute}
            disabled={isLoading}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full transition-colors',
              'text-red-600 hover:bg-red-100',
              isLoading && 'cursor-not-allowed opacity-50',
            )}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            title="Reject link"
          >
            {rejectAction.isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <X className="h-3.5 w-3.5" />
            )}
          </motion.button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SuggestedLinkBadge;
